import { defineStore } from 'pinia'
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth'
import { auth } from '../config/firebase'
import api from '../services/api'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    firebaseUser: null,
    isAuthenticated: false,
    isAuthorized: false,
    token: null,
    error: null,
    loading: false
  }),
  
  getters: {
    // Check if user is admin
    isAdmin: (state) => {
      return state.user && (state.user.rol === 'ADMIN' || state.user.rol === 'admin')
    },
    
    // Get current user info
    currentUser: (state) => state.user,
    
    // Check if user is authenticated and authorized
    canAccess: (state) => state.isAuthenticated && state.isAuthorized
  },
  
  actions: {
    // Login with Firebase
    async login(email, password) {
      this.loading = true
      this.error = null
      
      try {
        // Authenticate with Firebase
        const userCredential = await signInWithEmailAndPassword(auth, email, password)
        this.firebaseUser = userCredential.user
        
        // Get Firebase token
        this.token = await userCredential.user.getIdToken()
        
        // Verify authorization with backend
        const verifyResponse = await api.verifyAuth()
        
        if (verifyResponse.success && verifyResponse.data.autorizado) {
          this.isAuthorized = true
          
          // Get user details from backend
          const userResponse = await api.getCurrentUser()
          
          if (userResponse.success) {
            this.user = userResponse.data
            this.isAuthenticated = true
            
            // Store in localStorage
            localStorage.setItem('firebase_user', JSON.stringify({
              uid: this.firebaseUser.uid,
              email: this.firebaseUser.email
            }))
            
            return true
          } else {
            throw new Error('No se pudo obtener información del usuario')
          }
        } else {
          // User is authenticated in Firebase but not authorized in backend
          this.isAuthorized = false
          this.error = 'Usuario no autorizado. Contacte al administrador.'
          await this.logout()
          return false
        }
      } catch (error) {
        console.error('Error en login:', error)
        this.error = this.getErrorMessage(error)
        this.isAuthenticated = false
        this.isAuthorized = false
        return false
      } finally {
        this.loading = false
      }
    },
    
    // Check if user is already authenticated
    async checkAuth() {
      return new Promise((resolve) => {
        onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser) {
            this.firebaseUser = firebaseUser
            this.token = await firebaseUser.getIdToken()
            
            try {
              // Verify with backend
              const verifyResponse = await api.verifyAuth()
              
              if (verifyResponse.success && verifyResponse.data.autorizado) {
                this.isAuthorized = true
                
                // Get user details
                const userResponse = await api.getCurrentUser()
                
                if (userResponse.success) {
                  this.user = userResponse.data
                  this.isAuthenticated = true
                  resolve(true)
                  return
                }
              }
            } catch (error) {
              console.error('Error verificando autenticación:', error)
            }
          }
          
          // If we get here, user is not authenticated or not authorized
          this.isAuthenticated = false
          this.isAuthorized = false
          this.user = null
          resolve(false)
        })
      })
    },
    
    // Logout
    async logout() {
      try {
        await signOut(auth)
      } catch (error) {
        console.error('Error en logout:', error)
      } finally {
        this.user = null
        this.firebaseUser = null
        this.isAuthenticated = false
        this.isAuthorized = false
        this.token = null
        this.error = null
        
        // Remove from localStorage
        localStorage.removeItem('firebase_user')
      }
    },
    
    // Get user-friendly error message
    getErrorMessage(error) {
      const errorCode = error.code
      
      switch (errorCode) {
        case 'auth/invalid-email':
          return 'Correo electrónico inválido'
        case 'auth/user-disabled':
          return 'Usuario deshabilitado'
        case 'auth/user-not-found':
          return 'Usuario no encontrado'
        case 'auth/wrong-password':
          return 'Contraseña incorrecta'
        case 'auth/invalid-credential':
          return 'Credenciales inválidas'
        case 'auth/too-many-requests':
          return 'Demasiados intentos. Intente más tarde'
        default:
          return error.message || 'Error al iniciar sesión'
      }
    }
  }
})
